/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import {
	ACTION_DELEGATE_STATE,
	IActionDebugData,
	IActionResultErrorObject,
	IResourceDescription
} from "@adapptio/lib-core";
import {
	createEventEmitter,
	emitEvent,
	isNonEmptyArray,
	offEvent,
	onEvent,
	removeAllEventListeners,
	TGetSimpleEventEmitterDataType,
	TSimpleEventListener
} from "@adapptio/lib-shared";
import { IEditor } from "../../../IEditor";
import { ResourceService } from "../../../resources/ResourceService";
import { IEditorResource_Action, IEditorResource_Base } from "../../../resources/ResourceTypes";
import { RuntimeService } from "../../../runtime/RuntimeService";
import { LayoutState, LayoutState_Tabs } from "../../../ui/components/Layout/LayoutState";
import { IView, IViewEditable, IViewInfo } from "../../../view/IView";
import { ActionEditorLayoutConfig } from "./Layout";
import { ACTION_MODEL_MODE, ActionModel } from "../Common/ActionModel";
import { ViewService } from "../../../view/ViewService";
import { IntegrationService } from "../../../integration/IntegrationService";
import { IIntegrationFunction, IIntegrationType } from "../../../integration/IntegrationServiceTypes";
import { BrowserLogger } from "../../../common/BrowserLogger";
import { isSchemaFlowNodeModel, TTypeDesc } from "../../../../../lib-blueprint/src";
import { ABILITY_ACTIONS, AbilityService } from "../../../ability/AbilityService";
import { ICopyData, IPasteData, OnCopyData, OnPasteData } from "../../../copyPaste";
import { isSelectionItemActionFlowNode, isSelectionItemActionParameter } from "../Common/ActionEditSelection";
import { BP_IDT_TYPE } from "@adapptio/lib-blueprint/src/IDT/ISchemaIDT";
import { EnvironmentService } from "../../../environment/EnvironmentService";

interface IActionEditorViewParams {
	resourceId: string;
}

export interface IActionEditorViewExecutionResult {
	state: ACTION_DELEGATE_STATE;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	data?: any;
	type?: TTypeDesc;
	debugData?: IActionDebugData;
	lastError: IActionResultErrorObject;
}

export enum ACTION_EDITOR_VIEW_MODE {
	DESIGN = "design",
	DEBUG = "debug"
}

export class ActionEditorView implements IView<IActionEditorViewParams>, IViewEditable {
	/* Ability */
	private abilitySvc: AbilityService;

	/* Services */
	private logger: BrowserLogger;
	private resourceSvc: ResourceService;
	private runtimeSvc: RuntimeService;
	private integrationSvc: IntegrationService;
	private viewSvc: ViewService;

	/* Resource */
	private resourceDescriptor: IResourceDescription;
	private resource: IEditorResource_Action;

	/** View info change event */
	public onViewInfoChanged = createEventEmitter<void>();

	/** Emitted when resource descriptor changed */
	public onResourceInfoChanged = createEventEmitter<void>();

	/** Emitted when action has been executed */
	public onExecuted = createEventEmitter<void>();

	/** Emitted when execution status changes */
	public onExecutionStatusChange = createEventEmitter<void>();

	/** Emitted when editor mode change */
	public onModeChange = createEventEmitter();

	/* INTERNAL: Emit to zoom flow editor */
	public __onFlowEditorZoomIn = createEventEmitter<void>();
	public __onFlowEditorZoomOut = createEventEmitter<void>();
	public __onFlowEditorZoomReset = createEventEmitter<void>();
	public __onFlowEditorZoomFit = createEventEmitter<void>();

	/** Document model */
	private actionModel: ActionModel;

	/** Layout state */
	private layout: LayoutState;

	/** Action editor mode */
	private editorMode: ACTION_EDITOR_VIEW_MODE = ACTION_EDITOR_VIEW_MODE.DESIGN;

	/** If resource was modified */
	public isModified: boolean;

	/** If action is being saved */
	private saveInProgress = false;

	/** If action is being executed */
	private executionInProgress = false;

	/** Execution result */
	private executionResult: IActionEditorViewExecutionResult = null;

	private envSvc: EnvironmentService;

	/* Resource event handlers */
	private handleResourceChange: () => void;
	private handleResourceDelete: TSimpleEventListener<
		TGetSimpleEventEmitterDataType<IEditorResource_Base["onDelete"]>
	>;

	/**
	 * View constructor
	 *
	 * @param dc Dependency Container
	 */
	public constructor(dc: IEditor) {
		this.abilitySvc = dc.get("abilityService");
		this.logger = dc.get("logger").facility("ActionEditorView");
		this.resourceSvc = dc.get("resourceService");
		this.runtimeSvc = dc.get("runtimeService");
		this.integrationSvc = dc.get("integrationService");
		this.viewSvc = dc.get("viewService");
		this.envSvc = dc.get("environmentService");

		this.layout = new LayoutState(ActionEditorLayoutConfig);
	}

	/**
	 * View init
	 *
	 * @param params Params
	 */
	public async init(params: IActionEditorViewParams, viewId: string): Promise<void> {
		this.resourceDescriptor = this.resourceSvc.getResourceDescriptorById(params.resourceId);
		this.resource = (await this.resourceSvc.getBlueprintById(
			params.resourceId
		)) as IEditorResource_Action;

		emitEvent(this.onViewInfoChanged);

		if (!this.resource) {
			throw new Error(`Resource '${params.resourceId}' was not found.`);
		}

		this.actionModel = new ActionModel(this.runtimeSvc, this.integrationSvc, {});
		await this.actionModel.loadDocument(this.resource.content);

		onEvent(this.actionModel.onModelChange, () => {
			this.setIsModified(true, true);
		});

		onEvent(this.actionModel.getSelection().onSelectSingle, () => {
			this.layout.getById<LayoutState_Tabs>("panelRight").selectTab(1);
		});

		onEvent(this.actionModel.getSelection().onSelectSelected, () => {
			this.layout.getById<LayoutState_Tabs>("panelRight").selectTab(1);
		});

		onEvent(this.actionModel.getSelection().onUnselectAll, () => {
			this.layout.getById<LayoutState_Tabs>("panelRight").selectTab(0);
		});

		onEvent(
			this.resource.onChange,
			(this.handleResourceChange = () => {
				this.resourceDescriptor = this.resource.descriptor;
				emitEvent(this.onViewInfoChanged);
				emitEvent(this.onResourceInfoChanged);
			})
		);

		onEvent(
			this.resource.onDelete,
			(this.handleResourceDelete = ({ deletedByLocalAction }) => {
				if (deletedByLocalAction || !this.isModified) {
					this.isModified = false;
					this.viewSvc.close(viewId);
				}
			})
		);
	}

	public deselectAllEventNode() {
		this.layout.getById<LayoutState_Tabs>("panelRight").selectTab(0);
	}

	private emitViewInfoChanged(): void {
		emitEvent(this.onViewInfoChanged);
	}

	/**
	 * Returns action model
	 */
	public getActionModel(): ActionModel {
		return this.actionModel;
	}

	/**
	 * Returns layout state
	 */
	public getLayout(): LayoutState {
		return this.layout;
	}

	/**
	 * Returns resource descriptor
	 */
	public getResourceDescriptor(): IResourceDescription {
		return this.resourceDescriptor;
	}

	/**
	 * Returns resource
	 */
	public getResource(): IEditorResource_Action {
		return this.resource;
	}

	/**
	 * Returns current editor mode
	 */
	public getMode(): ACTION_EDITOR_VIEW_MODE {
		return this.editorMode;
	}

	/**
	 * Sets the editor mode
	 *
	 * @param editorMode New editor mode
	 */
	public setMode(editorMode: ACTION_EDITOR_VIEW_MODE): void {
		switch (editorMode) {
			case this.editorMode:
				return;
			case ACTION_EDITOR_VIEW_MODE.DESIGN:
				this.actionModel.setMode(ACTION_MODEL_MODE.EDIT);
				break;
			default:
				break;
		}

		this.editorMode = editorMode;

		this.emitModeChange();
	}

	/**
	 * Emits event when editor mode changes
	 */
	private emitModeChange(): void {
		emitEvent(this.onModeChange);
	}

	/**
	 * Returns a list of parent resource descriptors
	 */
	public getResourceParentList(): IResourceDescription[] {
		return this.resourceSvc.getResourceParentList(this.resource.id);
	}

	/**
	 * Returns integration types
	 */
	public getIntegrationTypesList(): IIntegrationType[] {
		return this.integrationSvc.getIntegrationTypes();
	}

	/**
	 * Returns single intergration type
	 *
	 * @param integrationType Integration type name
	 */
	public getIntegrationType(integrationType: string): IIntegrationType {
		return this.integrationSvc.getIntegrationType(integrationType);
	}

	/**
	 * Returns spefici integration function
	 *
	 * @param integrationType Integration type name
	 * @param functionName Function name
	 */
	public getIntegrationFunction(integrationType: string, functionName: string): IIntegrationFunction {
		return this.integrationSvc.getSingleIntegrationFunction(integrationType, functionName);
	}

	/**
	 * Returns related resource description
	 * @param resourceId Resource ID
	 */
	public getRelatedResourceDescrption(resourceId: string): IResourceDescription {
		return this.resourceSvc.getResourceDescriptorById(resourceId);
	}

	/**
	 * Returns resource service
	 */
	public getResourceService(): ResourceService {
		return this.resourceSvc;
	}

	/**
	 * Saves changes
	 * Returns false if save is already in progress or no changes were made.
	 */
	public async save(): Promise<boolean> {
		if (!this.resource) {
			throw new Error("Resource was not loaded.");
		}

		if (!this.isModified || this.saveInProgress) {
			return false;
		}

		const content = this.actionModel.exportDocumentAsYAML();

		this.saveInProgress = true;
		emitEvent(this.onResourceInfoChanged);

		this.setIsModified(false, false);

		try {
			await this.resourceSvc.saveBlueprint(
				this.resource.id,
				content,
				this.resource.metaData.revision,
				"Editor Save"
			);

			this.setIsModified(this.isModified, true);
		} catch (err) {
			this.setIsModified(true, true);
			throw err;
		} finally {
			this.saveInProgress = false;

			emitEvent(this.onResourceInfoChanged);
		}

		return true;
	}

	/**
	 * Returns if action is being saved
	 */
	public isBeingSaved(): boolean {
		return this.saveInProgress;
	}

	public canSave(): boolean {
		return (
			this.isModified &&
			!this.isBeingSaved() &&
			this.abilitySvc.can(ABILITY_ACTIONS.WRITE, this.resource)
		);
	}

	/**
	 * Executes an action
	 * Returns false if an action is already being executed
	 */
	public async execute(): Promise<boolean> {
		if (this.executionInProgress || !this.resource) {
			return false;
		}

		this.executionInProgress = true;
		emitEvent(this.onExecutionStatusChange);

		try {
			// Get params
			const params = await this.actionModel.exportParamsData();

			// Clear debug data
			this.actionModel.clearDebugData();

			// Call action
			const delegate = this.runtimeSvc.getActionRepository().getDelegate(this.resource.id, params);
			await delegate.invoke(true);
			await this.envSvc.reloadSession();

			this.executionResult = {
				state: delegate.getState(),
				data: delegate.getData(),
				type: delegate.getTypeDescriptor(),
				debugData: delegate.getDebugData(),
				lastError: delegate.getLastError()
			};

			const debugData = delegate.getDebugData();

			if (debugData) {
				this.actionModel.assignDebugData(debugData);
			}

			this.executionInProgress = false;
			emitEvent(this.onExecutionStatusChange);
			emitEvent(this.onExecuted);
			return true;
		} catch (err) {
			this.logger.warn("Failed to execute action:", err);
			this.executionInProgress = false;
			emitEvent(this.onExecutionStatusChange);

			throw err;
		}
	}

	/**
	 * Returns if action is being executed
	 */
	public isBeingExecuted(): boolean {
		return this.executionInProgress;
	}

	/**
	 * Returns last execution result object
	 */
	public getExecutionResult(): IActionEditorViewExecutionResult {
		return this.executionResult;
	}

	/**
	 * Zoom in the flow editor
	 */
	public flowEditorZoomIn(): void {
		emitEvent(this.__onFlowEditorZoomIn);
	}

	/**
	 * Zoom out the flow editor
	 */
	public flowEditorZoomOut(): void {
		emitEvent(this.__onFlowEditorZoomOut);
	}

	/**
	 * Reset zoom of the flow editor
	 */
	public flowEditorZoomReset(): void {
		emitEvent(this.__onFlowEditorZoomReset);
	}

	/**
	 * Fits the flow editor zoom
	 */
	public flowEditorZoomFit(): void {
		emitEvent(this.__onFlowEditorZoomFit);
	}

	@OnCopyData()
	public handleCopyData(): ICopyData {
		const actionModel = this.getActionModel();
		const selection = actionModel.getSelection();
		const dCtx = actionModel.getDesignContext();

		const items = selection.getItems();

		const result: ICopyData = {};

		for (let i = 0; i < items.length; i++) {
			const selectionItem = items[i];

			if (isSelectionItemActionParameter(selectionItem)) {
				result.parameters = result.parameters || [];
				result.parameters.push(selectionItem.mapEntry);
				continue;
			}

			if (isSelectionItemActionFlowNode(selectionItem)) {
				result.flowNodes = result.flowNodes || [];
				const model = dCtx.getNodeById(selectionItem.nodeId);

				const clonedModel = model.schema.clone(dCtx, model, model.parent);

				if (isSchemaFlowNodeModel(clonedModel)) {
					clonedModel.outputs = clonedModel.outputs.schema.createDefault(dCtx, clonedModel);
					result.flowNodes.push(clonedModel);
				}

				continue;
			}
		}

		return result;
	}

	@OnPasteData()
	public handlePasteData(data: IPasteData): void {
		const actionModel = this.getActionModel();
		const bpSchemaModel = actionModel.getModel();
		const dCtx = actionModel.getDesignContext();

		const viewProps = bpSchemaModel?.props.spec.props;

		const paramsMapModel = viewProps.params.props.props.props;
		const flowNodeListModel = viewProps.nodes.value;

		if (isNonEmptyArray(data.flowNodes)) {
			for (let i = 0; i < data.flowNodes.length; i++) {
				const modelIdt = data.flowNodes[i];

				const itemModel = flowNodeListModel.schema.flowNodeSchema.parse(dCtx, modelIdt, null);

				itemModel.id = `${itemModel.id}${Math.random() * 1000}`;
				let idIndex = 0;

				while (flowNodeListModel.items.some((item) => item.id === itemModel.id)) {
					idIndex = idIndex + 1;
					itemModel.id = itemModel.id + idIndex;
				}

				while (
					flowNodeListModel.items.some(
						(item) =>
							item.position.props.x.value === itemModel.position.props.x.value &&
							item.position.props.y.value === itemModel.position.props.y.value
					)
				) {
					itemModel.position.props.x.schema.setValue(
						itemModel.position.props.x,
						itemModel.position.props.x.value + 20
					);
					itemModel.position.props.y.schema.setValue(
						itemModel.position.props.y,
						itemModel.position.props.y.value + 20
					);
				}

				flowNodeListModel.schema.addItemModel(flowNodeListModel, itemModel, true);
			}
		}

		if (isNonEmptyArray(data.parameters)) {
			for (let i = 0; i < data.parameters.length; i++) {
				const mapEntryIdt = data.parameters[i];

				const valueModel = paramsMapModel.schema.opts.value.parse(dCtx, mapEntryIdt.value, null);

				const keyValue =
					mapEntryIdt.key.type === BP_IDT_TYPE.SCALAR ? mapEntryIdt.key.value : "parameter";

				paramsMapModel.schema.addElementModel(
					paramsMapModel,
					paramsMapModel.schema.getUniqueKey(paramsMapModel, String(keyValue), true),
					valueModel,
					true
				);
			}
		}
	}

	/**
	 * Disposes view
	 */
	public dispose(): void {
		if (this.actionModel) {
			this.actionModel.dispose();
		}

		removeAllEventListeners(this.onViewInfoChanged);
		removeAllEventListeners(this.onResourceInfoChanged);
		removeAllEventListeners(this.onExecuted);
		removeAllEventListeners(this.onExecutionStatusChange);

		removeAllEventListeners(this.__onFlowEditorZoomIn);
		removeAllEventListeners(this.__onFlowEditorZoomOut);
		removeAllEventListeners(this.__onFlowEditorZoomReset);
		removeAllEventListeners(this.__onFlowEditorZoomFit);

		if (this.resource) {
			offEvent(this.resource.onChange, this.handleResourceChange);
			offEvent(this.resource.onDelete, this.handleResourceDelete);
		}

		this.layout.dispose();
	}

	private setIsModified(isModified: boolean, notify: boolean) {
		this.isModified = isModified;

		if (notify) {
			emitEvent(this.onViewInfoChanged);
		}
	}

	/**
	 * Returns if action has been modified
	 */
	public getIsModified(): boolean {
		return this.isModified;
	}

	/**
	 * Returns if a current view is active
	 */
	public isViewActive(): boolean {
		return this.viewSvc.getCurrentView() === this;
	}

	/**
	 * Returns view info
	 */
	public getViewInfo(): IViewInfo {
		return {
			label: this.resourceDescriptor?.name || "...",
			icon: "mdi/motion-play",
			canClose: true,
			canSave: this.canSave(),
			isModified: this.isModified,
			renderWhenNotActive: true,
			tabStyleModifier: {
				"resource-type-action": true
			}
		};
	}

	public undo() {
		this.actionModel.undoRedoManager.undo();
		this.setIsModified(true, true);
	}

	public redo() {
		this.actionModel.undoRedoManager.redo();
		this.setIsModified(true, true);
	}

	public canUndo() {
		return this.actionModel.undoRedoManager.canUndo();
	}

	public canRedo() {
		return this.actionModel.undoRedoManager.canRedo();
	}
}
